import type { GitHunk } from "gitpod-next-api/supervisor/v1/environmentopts_pb";
import { cva } from "class-variance-authority";
import { useMemo } from "react";
import { LINE_MARKER_ADDED, LINE_MARKER_REMOVED, LINE_MARKER_UNCHANGED } from "@/routes/environments/code-changes/diff-utils";

enum LineChangeKind {
    Added = "Added",
    Removed = "Removed",
    Unchanged = "Unchanged",

    /**
     * A line that represents a break between hunks
     */
    Break = "Break",
}

type LineChange = {
    kind: LineChangeKind;
    key: string;
    originalLineNumber: number;
    newLineNumber: number;
    line: string;
};

const lineContentClass = cva("px-2 flex-1 text-sm", {
    variants: {
        kind: {
            [LineChangeKind.Added]: "bg-surface-success-subtle/50 text-content-success",
            [LineChangeKind.Removed]: "bg-surface-destructive-subtle/10 text-content-destructive",
            [LineChangeKind.Unchanged]: "bg-surface-secondary-subtle text-content-secondary",
            [LineChangeKind.Break]: "bg-surface-muted",
        },
    },
});

const lineNumberClass = cva("px-2 w-full justify-self-center text-end border-r border-r-border-base text-sm select-none font-mono", {
    variants: {
        kind: {
            [LineChangeKind.Added]: "bg-surface-success-subtle text-content-success",
            [LineChangeKind.Removed]: "bg-surface-destructive-subtle/50 text-content-destructive",
            [LineChangeKind.Unchanged]: "bg-surface-secondary text-content-secondary",
            [LineChangeKind.Break]: "bg-surface-secondary text-content-secondary border-r-0",
        },
    },
});

const GitDiffView = ({ hunks }: { hunks: GitHunk[] }) => {
    const codeLine = useMemo(() => {
        const lineChanges: LineChange[] = [];
        for (let i = 0; i < hunks.length; i++) {
            const hunk = hunks[i];
            const lines = hunk.body.split("\n");
            let originalLineOffset = -1;
            let newLineOffset = -1;
            for (const line of lines) {
                if (line === "") continue;
                let changeKind: LineChangeKind;
                switch (line[0]) {
                    case LINE_MARKER_ADDED:
                        changeKind = LineChangeKind.Added;
                        newLineOffset += 1;
                        break;
                    case LINE_MARKER_REMOVED:
                        changeKind = LineChangeKind.Removed;
                        originalLineOffset += 1;
                        break;
                    case LINE_MARKER_UNCHANGED:
                        changeKind = LineChangeKind.Unchanged;
                        originalLineOffset += 1;
                        newLineOffset += 1;
                        break;
                    default:
                        throw new Error(`Invalid line marker: ${line[0]}`);
                }
                const originalLineNumber = originalLineOffset + hunk.originalStartLine;
                const newLineNumber = newLineOffset + hunk.newStartLine;
                const key = `${changeKind}-${originalLineNumber}-${newLineNumber}`;
                lineChanges.push({
                    key,
                    originalLineNumber,
                    newLineNumber,
                    kind: changeKind,
                    line: line.slice(1),
                });
            }

            if (i !== hunks.length - 1) {
                lineChanges.push({
                    key: `break-${hunk.originalStartLine}-${hunk.newStartLine}`,
                    originalLineNumber: 0,
                    newLineNumber: 0,
                    kind: LineChangeKind.Break,
                    line: "",
                });
            }
        }
        return lineChanges;
    }, [hunks]);

    return (
        <div className="grid grid-cols-[auto_minmax(min-content,1fr)] overflow-auto">
            {codeLine.map((line) => (
                <GitDiffViewLine key={line.key} line={line} />
            ))}
        </div>
    );
};

const GitDiffViewLine = ({ line }: { line: LineChange }) => {
    let lineNumber: string;
    switch (line.kind) {
        case LineChangeKind.Removed:
            lineNumber = line.originalLineNumber.toString();
            break;
        case LineChangeKind.Unchanged:
        case LineChangeKind.Added:
            lineNumber = line.newLineNumber.toString();
            break;
        case LineChangeKind.Break:
            lineNumber = "-";
            break;
    }

    return (
        <>
            <span className={lineNumberClass({ kind: line.kind })}>{lineNumber}</span>
            <pre className={lineContentClass({ kind: line.kind })} key={line.line}>
                {line.line}
            </pre>
        </>
    );
};

export { GitDiffView };
